/* eslint-disable react/prop-types */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Modal, Form, Input, Button, message, Typography } from "antd";

const { Paragraph } = Typography;
import firebase from "firebase";
import { db_user, auth } from "../firestore_user";

const LeadCaptureContext = createContext(() => {});

export function useLeadCapture() {
  return useContext(LeadCaptureContext);
}

function LeadCaptureModal({ visible, onClose, prefill }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        name: prefill?.name || "",
        email: prefill?.email || "",
      });
    }
  }, [visible, prefill, form]);

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      setLoading(true);
      const uid = auth?.currentUser?.uid || null;
      await db_user.collection("black-leads").add({
        name: v.name.trim(),
        email: v.email.trim(),
        screenerLink: v.screenerLink.trim(),
        status: "default",
        userId: uid,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setLoading(false);
      message.success(
        "You're on the list. We're working through the queue and will get back to you shortly."
      );
      form.resetFields();
      onClose();
    } catch (e) {
      if (e?.errorFields) {
        setLoading(false);
        return;
      }
      setLoading(false);
      console.error(e);
      message.error("Could not save your details. Please try again.");
    }
  };

  return (
    <Modal
      title="Request Distribution Access"
      visible={visible}
      onCancel={onClose}
      wrapClassName="vc-lead-modal-wrap"
      className="vc-lead-modal"
      maskStyle={{ background: "rgba(0, 0, 0, 0.72)" }}
      bodyStyle={{ paddingTop: 8 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Request Access
        </Button>,
      ]}
      destroyOnClose
    >
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        We&apos;re receiving a very high volume of entries. Submit your film to
        the queue and we&apos;ll review it as soon as we can. You&apos;ll hear
        from us shortly.
      </Paragraph>
      <Form form={form} layout="vertical" style={{ marginTop: 0 }}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter your name" }]}
        >
          <Input placeholder="Your name" autoComplete="name" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, type: "email", message: "Valid email required" },
          ]}
        >
          <Input placeholder="you@example.com" autoComplete="email" />
        </Form.Item>
        <Form.Item
          name="screenerLink"
          label="Screener link"
          rules={[
            { required: true, message: "Screener link is required" },
            {
              validator: (_, value) => {
                const t = (value || "").trim();
                if (!t || !/^https?:\/\/.+/i.test(t)) {
                  return Promise.reject(new Error("Enter a valid http(s) URL"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="https://..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function LeadCaptureProvider({ children }) {
  const [visible, setVisible] = useState(false);
  const [prefill, setPrefill] = useState({});
  const openLeadCapture = useCallback((opts) => {
    if (opts && typeof opts === "object") {
      setPrefill({
        name: opts.name || "",
        email: opts.email || "",
      });
    } else {
      setPrefill({});
    }
    setVisible(true);
  }, []);
  const close = useCallback(() => {
    setVisible(false);
    setPrefill({});
  }, []);

  return (
    <LeadCaptureContext.Provider value={openLeadCapture}>
      {children}
      <LeadCaptureModal visible={visible} onClose={close} prefill={prefill} />
    </LeadCaptureContext.Provider>
  );
}
